import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  CITY,
  EMAIL,
  GEOGRAPHICAL_REGION,
  MOBILE,
  NAME,
  ROLE,
  STATE,
} from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import {
  useAddEmployeeManagementMutation,
  useUpdateEmployeeQuery,
  useEmployeeQuery
} from "@/services/employeeManagement";
import { useGeoRegionsQuery } from "@/services/masterData";
import { stateList } from "@/consts/stateList";
import { useState, useEffect, useRef } from "react";
import { useGetActiveGeoRegions } from "@/services/masterData/useGetActiveGeoRegions";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const isView = action == routes.view;
  const isEdit = action == routes.edit;
  const isMobile = useIsMobile();
  const [selectedState, setSelectedState] = useState("");
  // Guard to avoid resetting CITY during initial detail load
  const initializedFromDetailRef = useRef<boolean>(false);

  const createEmployeeMutation = useAddEmployeeManagementMutation({
    onSuccess: () => {
      // Navigate back after successful creation and table refresh
      onBackClick();
    },
  });

  const updateEmployeeMutation = useUpdateEmployeeQuery({
    onSuccess: () => {
      // Navigate back after successful update and table refresh
      onBackClick();
    },
  });

  // Fetch geographical regions
  const {
    data: geoRegionsData,
    isLoading: isLoadingGeoRegions,
    error: geoRegionsError,
  } = useGetActiveGeoRegions();

  // Fetch employee data for editing
  const {
    data: employeeData,
    isLoading: isLoadingEmployee,
    error: employeeError,
  } = useEmployeeQuery(id ?? "", {
    enabled: isEdit && !!id,
  });

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [EMAIL]: "",
      [MOBILE]: "",
      [ROLE]: [],
      [GEOGRAPHICAL_REGION]: "",
      [STATE]: "",
      [CITY]: "",
    },
  });

  // Populate form with existing data when editing
  useEffect(() => {
    if (isEdit && employeeData?.data) {
      const employee = employeeData.data;
      initializedFromDetailRef.current = true;
      setSelectedState(employee.state || "");
      form.reset({
        [NAME]: employee.name || "",
        [EMAIL]: employee.email || "",
        [MOBILE]: employee.mobile_number || "",
        [ROLE]: employee.role || [],
        [GEOGRAPHICAL_REGION]:
          employee.geographical_region_id != null
            ? String(employee.geographical_region_id)
            : "",
        [STATE]: employee.state || "",
        [CITY]: employee.city || "",
      });
    }
  }, [isEdit, employeeData, form]);

  const requiredFields = getRequiredFields(schema);

  // Watch state changes to update city options
  const watchedState = form.watch(STATE);

  useEffect(() => {
    // During initial detail load, sync selectedState without clearing CITY
    if (initializedFromDetailRef.current) {
      setSelectedState(watchedState || "");
      initializedFromDetailRef.current = false;
      return;
    }
    if (watchedState !== selectedState) {
      setSelectedState(watchedState || "");
      // Reset city when user changes state
      form.setValue(CITY, "");
    }
  }, [watchedState, form, selectedState]);

  // Prepare options for dropdowns
  const geographicalRegionOptions =
    geoRegionsData?.data?.map((region: any) => ({
      label: region.name,
      value: String(region.id),
    })) || [];

  const stateOptions = Object.keys(stateList).map((state) => ({
    label: state,
    value: state,
  }));

  const cityOptions = selectedState
    ? (stateList[selectedState as keyof typeof stateList] || []).map(
        (city) => ({
          label: city,
          value: city,
        })
      )
    : [];

  useEffect(() => {
    if (
      isEdit &&
      employeeData?.data?.geographical_region_id &&
      geographicalRegionOptions.length > 0
    ) {
      form.setValue(
        GEOGRAPHICAL_REGION,
        String(employeeData.data.geographical_region_id)
      );
      form.setValue(ROLE, employeeData.data.role);
    }
  }, [geographicalRegionOptions, isEdit, employeeData, form]);

  useEffect(() => {
    if (
      isEdit &&
      employeeData?.data?.state &&
      stateOptions.length > 0 &&
      cityOptions.length > 0
    ) {
      const currentState = form.getValues(STATE);
      const currentCity = form.getValues(CITY);

      if (currentState !== employeeData.data.state) {
        form.setValue(STATE, String(employeeData.data.state));
      }
      if (currentCity !== employeeData.data.city) {
        form.setValue(CITY, String(employeeData.data.city));
      }
    }
  }, [stateOptions, cityOptions, isEdit, employeeData, form]);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.employee}`);
  };

  const onSubmit = async (data: formValues) => {
    try {
      let _data = removeEmptyFields(data);
      _data = setEmptyFieldsToNull(_data);

      // Remove empty mobile number if not provided
      if (!_data[MOBILE]) {
        delete _data[MOBILE];
      }

      if (isEdit) {
        // For editing, exclude email from the payload and add the ID
        // Email is disabled during edit, so we don't include it in the update
        const updatePayload = {
          id: id!,
          name: String(_data[NAME] ?? ""),
          mobile_number: _data[MOBILE] ? String(_data[MOBILE]) : undefined,
          role: (Array.isArray(_data[ROLE]) ? _data[ROLE].flat() : []) as [],
          city: String(_data[CITY] ?? ""),
          state: String(_data[STATE] ?? ""),
          geographical_region_id: String(_data[GEOGRAPHICAL_REGION] ?? ""),
        };

        await updateEmployeeMutation.mutateAsync(updatePayload);
      } else {
        // For creating, include all fields including email
        const createPayload = {
          name: String(_data[NAME] ?? ""),
          email: String(_data[EMAIL] ?? ""),
          mobile_number: _data[MOBILE] ? String(_data[MOBILE]) : undefined,
          role: (Array.isArray(_data[ROLE]) ? _data[ROLE].flat() : []) as [],
          city: String(_data[CITY] ?? ""),
          state: String(_data[STATE] ?? ""),
          geographical_region_id: String(_data[GEOGRAPHICAL_REGION] ?? ""),
        };

        await createEmployeeMutation.mutateAsync(createPayload);
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isLoading:
      createEmployeeMutation.isPending || updateEmployeeMutation.isPending,
    geographicalRegionOptions,
    stateOptions,
    cityOptions,
    isLoadingGeoRegions,
    geoRegionsError,
    isLoadingEmployee,
    employeeError,
  };
};

export default useAddEdit;
